import { Component, ViewChild } from '@angular/core';
import { LedgerEntryDetailsComponent } from 'components/company/invoice/ledger-entry-details.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { ReasonSelectComponent } from 'components/input/reason-select/reason-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { InvoiceObjectionSearchRequestModel } from 'models/generated/Accounting.Api.Models.InvoiceObjectionSearchRequest';
import { CustomerLedgerEntryModel } from 'models/generated/BusinessCentral.Integration.Models.CustomerLedgerEntry';
import { InvoiceObjectionModel } from 'models/generated/Entities.Accounting.InvoiceObjection';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { BrandService } from 'services/brand.service';
import { InvoicingService } from 'services/invoicing.service';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl } from 'services/navigation.service';
import { ReasonService } from 'services/reason.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'app-invoice-objection-search',
	templateUrl: './invoice-objection-search.component.html',
	providers: [FormValidationService],
	imports: [Layout, ReasonSelectComponent, CustomInputComponent, ListView, LedgerEntryDetailsComponent, CurrencyPipe, TranslatePipe, CustomDatePipe, UserPipe]
})
@ComponentUrl('invoice-objection-search')
@CredentialRequired(CredentialType.SearchInvoiceObjections)
export class InvoiceObjectionSearchComponent {
	@ViewChild('searchButton') searchButton!: SubmitButtonComponent;

	searchRequest: InvoiceObjectionSearchRequestModel;
	customerEntries: CustomerLedgerEntryModel[] = [];
	invoiceObjections: InvoiceObjectionModel[] = [];
	objectionReasons = new Map<string, ReasonModel>();
	selectedInvoiceObjection?: InvoiceObjectionModel;
	inputTypeEnum = InputType;
	searching = true;

	constructor(
		private readonly invoicingService: InvoicingService,
		private readonly companyService: CompanyService,
		private readonly userService: UserService,
		private readonly brandService: BrandService,
		reasonService: ReasonService) {
		reasonService.getReasonMap().then(reasonMap => {
			// Includes all types, but that is necessary for old bindings
			this.objectionReasons = reasonMap;
		});

		this.searchRequest = new InvoiceObjectionSearchRequestModel();
		this.searchRequest.onlyOpenInvoices = true;
		this.search();
	}

	async search() {
		this.searching = true;
		this.selectedInvoiceObjection = undefined;
		this.invoicingService.searchInvoiceObjections(this.searchRequest).then(response => {
			this.invoiceObjections = response;
			if (this.invoiceObjections.length == 0) {
				this.resetSearch();
				return;
			}
			const documentNos = this.invoiceObjections.map(o => o.documentNo);
			const companyIds = this.invoiceObjections.filter(o => o.companyId).map(o => o.companyId!);
			const businessCentralBrandId = this.brandService.brands.find(b => b.shortName == 'GO')?.id;

			Promise.all([
				this.invoicingService.getCustomerLedgerEntriesByDocumentNos(documentNos, businessCentralBrandId!),
				this.companyService.getCompaniesByIds(companyIds)
			]).then(([ledgerEntries, companies]) => {

				this.invoiceObjections.forEach(o => {
					o.ledgerEntry = ledgerEntries.find(entry => entry.documentNo == o.documentNo);
					o.company = companies.find(company => company.id == o.companyId);
					o.user = this.userService.users.find(user => user.id == o.userId);
				});

				if (this.searchRequest.onlyOpenInvoices) {
					this.invoiceObjections = this.invoiceObjections.filter(o => o.ledgerEntry?.open);
				}

			})
			.catch(_ => this.invoiceObjections = [])
			.finally(() => this.resetSearch());
		});
	}

	resetSearch() {
		this.searchButton.reset();
		this.searching = false;
	}
}
